import {
  DeliveryMethod,
  FundingSource,
  InvoiceData,
  useDeliveryMethod,
  useFeesBreakdown,
  useFundingSource,
  useOrgBillingFeeSettings,
  usePaymentSettings,
  Vendor,
} from '@melio/platform-api';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { converDateToStringRepresentation } from '@melio/platform-utils';

import { convertPaymentFeeCentsToDollars } from '../../functions/fees';
import { usePaymentFee, useTotalFees } from './fees';

type PaymentData = {
  dueDate: Date;
  amount: number;
  invoiceNumber: string | null;
  vendorId: Vendor['id'];
  fundingSourceId: FundingSource['id'];
  deliveryMethodId: DeliveryMethod['id'];
  sourceEntity?: InvoiceData['sourceEntity'];
};

export const usePaymentFeesDetails = (payment: PaymentData) => {
  const { data: fundingSource, isLoading: isLoadingFundingSource } = useFundingSource({
    id: payment.fundingSourceId,
  });

  const { data: deliveryMethod, isLoading: isLoadingDeliveryMethod } = useDeliveryMethod({
    id: payment.deliveryMethodId,
  });

  const [isBillingFeeSettingsEnabled] = useFeature(FeatureFlags.BillingFees, false);
  const [shouldUseFeesBreakdownApi] = useFeature(FeatureFlags.CheckFeesPromotionEnabled, false);

  const { data: orgBillingFeeSettings = [], isLoading: isBillingFeeSettingsLoading } = useOrgBillingFeeSettings({
    enabled: isBillingFeeSettingsEnabled,
  });

  const {
    data: paymentsSettings,
    isLoading: isLoadingPaymentSettings,
    error: paymentsSettingsError,
  } = usePaymentSettings({
    params: [
      {
        dueDate: converDateToStringRepresentation(payment.dueDate), // TODO: this conversion should be on the API level
        amountToPay: payment.amount,
        vendorId: payment.vendorId,
        fundingSourceId: payment.fundingSourceId,
        deliveryMethodId: payment.deliveryMethodId,
        invoicesData: [
          {
            sourceEntity: payment.sourceEntity,
            invoiceNumber: payment.invoiceNumber,
            amount: payment.amount,
          },
        ],
      },
    ],
  });

  const paymentFees = convertPaymentFeeCentsToDollars(paymentsSettings?.[0]?.estimatedFees || []); // TODO: this conversion should be on the API level

  const deliveryPreference = paymentsSettings?.[0]?.deliveryPreferenceOptions?.[0];

  const shouldEnableFeesBreakdown = deliveryPreference && fundingSource && deliveryMethod && shouldUseFeesBreakdownApi;

  const {
    data: feesBreakdown,
    isLoading: isLoadingFeesBreakdown,
    error: feesBreakdownError,
  } = useFeesBreakdown({
    params: {
      feesBreakdownParams: shouldEnableFeesBreakdown
        ? [
            {
              deliveryMethodType: deliveryMethod.type,
              deliveryMethodId: deliveryMethod.id,
              fundingSourceType: fundingSource.type,
              fundingSourceId: fundingSource.id,
              deliveryPreference: deliveryPreference.type,
              paymentAmount: payment.amount,
            },
          ]
        : [],
    },
    enabled: shouldEnableFeesBreakdown,
  });

  return {
    feesBreakdown,
    error: paymentsSettingsError || feesBreakdownError,
    feeItems: usePaymentFee(paymentFees),
    total: useTotalFees(paymentFees, orgBillingFeeSettings),
    isLoading:
      isLoadingFundingSource ||
      isLoadingDeliveryMethod ||
      isBillingFeeSettingsLoading ||
      isLoadingPaymentSettings ||
      isLoadingFeesBreakdown,
  };
};
